import React, { useState, useEffect, useCallback } from 'react';
import { AiOutlineEdit, AiOutlineDelete } from 'react-icons/ai';
import { FiMapPin } from 'react-icons/fi';
import { apiDELETE, apiGET } from '../../utilities/apiHelpers';
import { useSelector } from 'react-redux';
import Popup from '../Address/Popup';
import EditPopup from '../Address/EditPopup';
import { toast } from 'react-toastify';

const AddressCard = () => {
  const usersid = useSelector(state => state?.user?.userData?.id);

  const [addressData, setAddressData] = useState([]);
  const [error, setError] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  
  // New States for Delete Modal
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);

  /* ================= FETCH ADDRESS ================= */
  const fetchAddress = useCallback(async () => {
  if (!usersid) return;
  try {
    const response = await apiGET(`v1/address/getAddress/${usersid}`);
    const data = response?.data?.data;
    
    // Handle both array and object responses
    let addressArray = [];
    if (Array.isArray(data)) {
      addressArray = data;
    } else if (data && typeof data === 'object') {
      // If it's an object, convert it to an array
      addressArray = Object.values(data);
    }
    
    console.log("Fetched addresses:", addressArray);
    setAddressData(addressArray);
  } catch (err) {
    console.error('Error fetching addresses:', err);
    setError(err.message);
    toast.error('Failed to load addresses');
    setAddressData([]);
  }
}, [usersid]);

  useEffect(() => {
    fetchAddress();
  }, [fetchAddress]);

  /* ================= HANDLERS ================= */
  const handleEdit = (address) => {
    setSelectedAddress(address);
    setIsEditPopupOpen(true);
  };

  // Trigger the Modal instead of browser confirm
  const handleDeleteClick = (id) => {
    setAddressToDelete(id);
    setOpenDeleteModal(true);
  };

  const confirmDeleteAddress = async () => {
    if (!addressToDelete) return;

    try {
      const res = await apiDELETE(`/v1/address/remove-address/${addressToDelete}`);
      if (res.status === 200) {
        setAddressData(prev => prev.filter(addr => addr._id !== addressToDelete));
        toast.success('Address deleted successfully');
      }
    } catch (error) {
      toast.error('Failed to delete address');
    } finally {
      setOpenDeleteModal(false);
      setAddressToDelete(null);
    }
  };

  const handleAdd = () => setIsPopupOpen(true);

  if (error) return <div className="text-red-500">Error: {error}</div>;

  return (
    <div className="flex flex-wrap items-center justify-center p-4">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-[793px]">

        {addressData.length === 0 && (
          <p className="text-center text-gray-500">
            No addresses found. Please add one.
          </p>
        )}

        {addressData.map((address) => (
          <div
            key={address._id}
            className="bg-[#F8F8F8] text-xs p-4 rounded-lg shadow-md relative my-5"
          >
            <div className="flex flex-col md:flex-row md:space-x-4">
              <FiMapPin className="text-xl text-[#095D7E] mb-2 md:mb-0" />
              <div className="flex-1">
                <div className="text-sm font-bold">{address?.zip}</div>
                <div className="text-sm">{address?.addressLine1}</div>
                {address?.addressLine2 && (
                  <div className="text-sm">{address.addressLine2}</div>
                )}
              </div>
            </div>

            <div className="absolute top-4 right-4 flex space-x-2">
              <button onClick={() => handleEdit(address)} className="text-green-500">
                <AiOutlineEdit className="text-xl" />
              </button>
              <button onClick={() => handleDeleteClick(address._id)} className="text-red-500">
                <AiOutlineDelete className="text-xl" />
              </button>
            </div>
          </div>
        ))}

        <div className="flex mt-6 justify-start w-full">
          <button
            onClick={handleAdd}
            className="py-[10px] px-6 mt-3 flex items-center justify-center bg-[#F1F9FF] text-[#14967F] text-sm font-bold rounded-full border border-[#14967F]/30 hover:bg-[#e6f4ff] transition-all active:scale-95 whitespace-nowrap"
          >
            + Add New Address
          </button>
        </div>
      </div>

      {/* Popups */}
      <Popup isOpen={isPopupOpen} setIsOpen={setIsPopupOpen} onUpdate={fetchAddress} />
      <EditPopup isOpen={isEditPopupOpen} setIsOpen={setIsEditPopupOpen} address={selectedAddress} onUpdate={fetchAddress} />

      {/* Custom Delete Modal */}
      {openDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden ">
            
            {/* Header */}
            <div className="bg-[#14967F] px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white tracking-wide">
                Delete Address
              </h3>
              <button
                onClick={() => setOpenDeleteModal(false)}
                className="text-white/80 p-1 rounded-lg hover:text-white transition-colors hover:bg-red-500"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-8">
              <div className="flex flex-col items-center text-center">
                <p className="text-gray-600 text-base leading-relaxed">
                  Are you sure you want to delete this address? <br />
                  <span className="font-semibold text-red-500">This action cannot be undone.</span>
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-8">
                <button
                  onClick={() => {
                    setOpenDeleteModal(false);
                    setAddressToDelete(null);
                  }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 active:scale-95 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteAddress}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 shadow-md shadow-red-200 active:scale-95 transition-all"
                >
                  Delete Now
                </button>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default AddressCard;